import React from 'react';
import { motion } from 'framer-motion';
import { FiMessageCircle, FiShoppingCart, FiSettings } from 'react-icons/fi';
import { FaWhatsapp, FaAmazon } from 'react-icons/fa';
import { useScrollAnim, V } from '../hooks/useScrollAnimation';

const WAYS = [
  {
    Icon: FaWhatsapp,
    iconColor: '#25D366',
    title: 'Business Procurement',
    label: 'Procurement Support',
    desc: 'Connect directly with our team for procurement planning, bulk requirements, and long-term supply partnerships.',
    points: ['Dedicated Account Manager', 'Bulk Pricing', 'GST Invoice', 'Fast Dispatch'],
    btnLabel: 'Connect on WhatsApp',
    btnHref: 'https://wa.me/919821991932?text=Hi, I want to discuss bulk procurement and long-term supply.',
    btnColor: 'linear-gradient(135deg,#25D366,#128C7E)',
  },
  {
    Icon: FaAmazon,
    iconColor: '#FF9900',
    title: 'Marketplace Availability',
    label: 'Digital Access',
    desc: 'SPE products are available on leading e-commerce platforms for convenient and quick ordering.',
    points: ['Meesho', 'Amazon', 'Flipkart', 'Easy Returns'],
    btnLabel: 'Shop Online',
    btnHref: 'https://www.amazon.in/s?k=shree+parasnath+enterprises',
    btnColor: 'linear-gradient(135deg,#FF9900,#FF6B2B)',
  },
  {
    Icon: FiSettings,
    iconColor: '#C4B5FD',
    title: 'Customized Solutions',
    label: 'Business Consulting',
    desc: 'Tailored packaging, sourcing, and supply programs designed around your specific business requirements.',
    points: ['Custom Sizes', 'Private Labeling', 'Volume Flexibility', 'Dedicated Support'],
    btnLabel: 'Request Consultation',
    btnHref: 'https://wa.me/919821991932?text=Hi, I need customized packaging solutions for my business.',
    btnColor: 'linear-gradient(135deg,#6B21A8,#E91E8C)',
  },
];

export default function Order() {
  const { ref, controls } = useScrollAnim(0.08);

  return (
    <section id="order" style={{
      padding: 'var(--spy) 0',
      background: 'linear-gradient(135deg, #2D0A54 0%, #4A148C 40%, #5E35B1 100%)',
      position: 'relative',
      overflow: 'hidden',
      color: '#fff'
    }}>
      {/* Background Elements */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
        backgroundSize: '28px 28px',
        pointerEvents: 'none'
      }} />

      <div style={{
        position: 'absolute',
        top: '-10%',
        right: '-10%',
        width: 520,
        height: 520,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(233,30,140,0.12) 0%, transparent 70%)',
        filter: 'blur(70px)',
        pointerEvents: 'none'
      }} />

      <div className="container" ref={ref}>
        {/* Header */}
        <motion.div
          initial="hidden"
          animate={controls}
          variants={V.stagger}
          style={{ textAlign: 'center', marginBottom: '4rem' }}
        >
          <motion.span
            variants={V.child}
            style={{
              display: 'block',
              fontFamily: 'var(--font-body)',
              fontSize: '0.78rem',
              fontWeight: 700,
              color: '#C4B5FD',
              letterSpacing: '0.15em',
              marginBottom: '0.8rem',
              textTransform: 'uppercase'
            }}
          >
            CONNECT WITH SPE
          </motion.span>

          <motion.h2
            variants={V.child}
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 'clamp(2.3rem, 5vw, 3.5rem)',
              lineHeight: 1.1,
              marginBottom: '1.1rem'
            }}
          >
            How We Engage With Clients
          </motion.h2>

          <motion.p
            variants={V.child}
            style={{
              fontFamily: 'var(--font-body)',
              color: 'rgba(255,255,255,0.65)',
              fontSize: '1.05rem',
              maxWidth: 620,
              margin: '0 auto',
              lineHeight: 1.7
            }}
          >
            Multiple professional channels designed to make procurement smooth and efficient for businesses.
          </motion.p>
        </motion.div>

        {/* Cards Grid */}
        <motion.div
          initial="hidden"
          animate={controls}
          variants={V.stagger2}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}
        >
          {WAYS.map((way, index) => (
            <motion.div
              key={index}
              variants={V.child}
              whileHover={{ y: -12, transition: { duration: 0.4 } }}
              style={{
                background: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(14px)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 26,
                padding: '2.4rem 2rem',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                transition: 'all 0.35s ease'
              }}
            >
              {/* Icon */}
              <div style={{
                width: 68,
                height: 68,
                borderRadius: 18,
                background: 'rgba(255,255,255,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.6rem'
              }}>
                <way.Icon size={34} style={{ color: way.iconColor }} />
              </div>

              {/* Label */}
              <span style={{
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: '#C4B5FD',
                marginBottom: '0.6rem',
                textTransform: 'uppercase'
              }}>
                {way.label}
              </span>

              {/* Title */}
              <h3 style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 700,
                fontSize: '1.45rem',
                lineHeight: 1.25,
                marginBottom: '1rem'
              }}>
                {way.title}
              </h3>

              {/* Description */}
              <p style={{
                fontFamily: 'var(--font-body)',
                color: 'rgba(255,255,255,0.7)',
                lineHeight: 1.75,
                marginBottom: '1.8rem',
                flex: 1
              }}>
                {way.desc}
              </p>

              {/* Points */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '2rem' }}>
                {way.points.map((pt, i) => (
                  <span
                    key={i}
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      background: 'rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.85)',
                      padding: '0.35rem 0.85rem',
                      borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.12)'
                    }}
                  >
                    {pt}
                  </span>
                ))}
              </div>

              {/* CTA Button */}
              <motion.a
                href={way.btnHref}
                target="_blank"
                rel="noreferrer"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  marginTop: 'auto',
                  padding: '1rem 1.8rem',
                  background: way.btnColor,
                  color: '#fff',
                  borderRadius: 14,
                  fontWeight: 700,
                  textAlign: 'center',
                  textDecoration: 'none',
                  fontSize: '0.95rem',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8
                }}
              >
                <FiMessageCircle size={16} /> {way.btnLabel}
              </motion.a>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom Strip - Multiple Channels */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{
            marginTop: '4.5rem',
            padding: '2rem 2.5rem',
            borderRadius: 22,
            background: 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.12)',
            textAlign: 'center'
          }}
        >
          <p style={{
            fontFamily: 'var(--font-body)',
            fontWeight: 700,
            fontSize: '0.85rem',
            color: 'rgba(255,255,255,0.6)',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            marginBottom: '1.2rem'
          }}>
            Serving Businesses Across Multiple Channels
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '3rem', flexWrap: 'wrap' }}>
            <motion.a href="https://www.amazon.in/s?k=shree+parasnath+enterprises" target="_blank" rel="noreferrer" whileHover={{ scale: 1.1 }}>
              <FaAmazon size={42} color="#FF9900" />
            </motion.a>
            <motion.a href="https://www.flipkart.com" target="_blank" rel="noreferrer" whileHover={{ scale: 1.1 }}>
              <span style={{ fontSize: '2.1rem', fontWeight: 700, color: '#fff' }}>FLIPKART</span>
            </motion.a>
            <motion.a href="https://www.meesho.com" target="_blank" rel="noreferrer" whileHover={{ scale: 1.1 }}>
              <span style={{ fontSize: '2rem', fontWeight: 700, color: '#FF3E6C' }}>meesho</span>
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}